'use client';

import { c as _c } from "react/compiler-runtime";
import type { HeaderGroup } from '@tanstack/react-table';
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';
import { mergeColumnOrder, reorderColumn } from './columnOrder';

// ─── Constants ────────────────────────────────────────────────────────

const DRAG_THRESHOLD = 5;
const SCROLL_ZONE = 60;
const SCROLL_SPEED = 10;

// ─── Types ────────────────────────────────────────────────────────────

interface DragState {
  columnId: string;
  startX: number;
  originIndex: number;
  headerIds: string[];
  rects: {
    id: string;
    left: number;
    width: number;
  }[];
  scrollParent: HTMLElement | null;
  initialScrollLeft: number;
}
interface UseColumnDragOptions {
  // Row-type-agnostic; HeaderGroup is invariant in TData, so `unknown` breaks
  // assignment from the concrete HeaderGroup at call sites.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headerGroups?: HeaderGroup<any, any>[];
  onColumnOrderChange?: (newOrder: string[]) => void;
  columnOrder?: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────

function getScrollParent(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent) {
    const {
      overflowX
    } = getComputedStyle(parent);
    if (overflowX === 'auto' || overflowX === 'scroll') return parent;
    parent = parent.parentElement;
  }
  return null;
}

// ─── Hook ─────────────────────────────────────────────────────────────

export function useColumnDrag(t0) {
  const $ = _c(26);
  const {
    headerGroups,
    onColumnOrderChange,
    columnOrder
  } = t0;
  const [draggedId, setDraggedId] = useState(null);
  const [deltaX, setDeltaX] = useState(0);
  const [targetIndex, setTargetIndex] = useState(null);
  const [originIndex, setOriginIndex] = useState(null);
  const [draggedWidth, setDraggedWidth] = useState(0);
  const dragRef = useRef(null);
  const isDraggingRef = useRef(false);
  const targetIndexRef = useRef(null);
  const scrollFrameRef = useRef(null);
  const lastPointerXRef = useRef(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const thRefs = useRef(t1);
  const isDraggable = !!onColumnOrderChange;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function stopAutoScroll() {
      if (scrollFrameRef.current != null) {
        cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const stopAutoScroll = t2;
  let t3;
  let t4;
  if ($[2] !== draggedId) {
    t3 = () => {
      document.body.style.cursor = draggedId ? "grabbing" : "";
      return () => {
        stopAutoScroll();
        document.body.style.cursor = "";
      };
    };
    t4 = [draggedId];
    $[2] = draggedId;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = function updateDragPosition(drag) {
      const scrollDelta = (drag.scrollParent?.scrollLeft ?? 0) - drag.initialScrollLeft;
      const dx = lastPointerXRef.current - drag.startX + scrollDelta;
      setDeltaX(dx);
      const pointerInScrollSpace = lastPointerXRef.current + scrollDelta;
      let newTargetIndex = drag.originIndex;
      for (let i = 0; i < drag.rects.length; i++) {
        const r = drag.rects[i];
        if (!r) {
          continue;
        }
        const center = r.left + r.width / 2;
        if (pointerInScrollSpace < center) {
          newTargetIndex = i;
          break;
        }
        newTargetIndex = i;
      }
      targetIndexRef.current = newTargetIndex;
      setTargetIndex(newTargetIndex);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const updateDragPosition = t5;
  let t6;
  if ($[6] !== headerGroups?.[0] || $[7] !== isDraggable) {
    t6 = function handlePointerDown(e, columnId) {
      if (!isDraggable) {
        return;
      }
      if ((e.target as HTMLElement).closest("[data-sort-button]")) {
        return;
      }
      const headerGroup = headerGroups?.[0];
      if (!headerGroup) {
        return;
      }
      const rects = headerGroup.headers.map(h => {
        const el = thRefs.current.get(h.id);
        const rect = el?.getBoundingClientRect() ?? {
          left: 0,
          width: 0
        };
        return {
          id: h.id,
          left: rect.left,
          width: rect.width
        };
      });
      const idx = headerGroup.headers.findIndex(h_0 => h_0.id === columnId);
      const scrollParent = getScrollParent(e.currentTarget);
      dragRef.current = {
        columnId,
        startX: e.clientX,
        originIndex: idx,
        headerIds: headerGroup.headers.map(_temp),
        rects,
        scrollParent,
        initialScrollLeft: scrollParent?.scrollLeft ?? 0
      };
      lastPointerXRef.current = e.clientX;
      isDraggingRef.current = false;
      e.currentTarget.setPointerCapture(e.pointerId);
    };
    $[6] = headerGroups?.[0];
    $[7] = isDraggable;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handlePointerDown = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function handlePointerMove(e_0) {
      const drag_0 = dragRef.current;
      if (!drag_0) {
        return;
      }
      lastPointerXRef.current = e_0.clientX;
      if (!isDraggingRef.current) {
        const dx_0 = e_0.clientX - drag_0.startX;
        if (Math.abs(dx_0) < DRAG_THRESHOLD) {
          return;
        }
        isDraggingRef.current = true;
        setDraggedId(drag_0.columnId);
        setOriginIndex(drag_0.originIndex);
        setDraggedWidth(drag_0.rects[drag_0.originIndex]?.width ?? 0);
      }
      updateDragPosition(drag_0);
      stopAutoScroll();
      const sp = drag_0.scrollParent;
      if (!sp) {
        return;
      }
      const spRect = sp.getBoundingClientRect();
      const distFromLeft = e_0.clientX - spRect.left;
      const distFromRight = spRect.right - e_0.clientX;
      if (distFromLeft < SCROLL_ZONE || distFromRight < SCROLL_ZONE) {
        const scrollDir = distFromLeft < SCROLL_ZONE ? -1 : 1;
        const scroll = () => {
          sp.scrollLeft = sp.scrollLeft + scrollDir * SCROLL_SPEED;
          if (dragRef.current) {
            updateDragPosition(dragRef.current);
          }
          scrollFrameRef.current = requestAnimationFrame(scroll);
        };
        scrollFrameRef.current = requestAnimationFrame(scroll);
      }
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handlePointerMove = t7;
  let t8;
  if ($[10] !== columnOrder || $[11] !== onColumnOrderChange) {
    t8 = function handlePointerUp(e_1) {
      const drag_1 = dragRef.current;
      if (drag_1 && isDraggingRef.current && onColumnOrderChange) {
        const finalTarget = targetIndexRef.current ?? drag_1.originIndex;
        if (finalTarget !== drag_1.originIndex) {
          const newVisibleOrder = reorderColumn(drag_1.headerIds, drag_1.originIndex, finalTarget);
          if (newVisibleOrder) {
            onColumnOrderChange(mergeColumnOrder(newVisibleOrder, columnOrder));
          }
        }
      }
      stopAutoScroll();
      if (e_1.currentTarget.hasPointerCapture(e_1.pointerId)) {
        e_1.currentTarget.releasePointerCapture(e_1.pointerId);
      }
      dragRef.current = null;
      isDraggingRef.current = false;
      targetIndexRef.current = null;
      setDraggedId(null);
      setDeltaX(0);
      setTargetIndex(null);
      setOriginIndex(null);
      setDraggedWidth(0);
    };
    $[10] = columnOrder;
    $[11] = onColumnOrderChange;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handlePointerUp = t8;
  let t9;
  if ($[13] !== deltaX || $[14] !== draggedId || $[15] !== draggedWidth || $[16] !== originIndex || $[17] !== targetIndex) {
    t9 = function getTranslateX(headerId, index) {
      if (!draggedId || originIndex == null) {
        return 0;
      }
      if (headerId === draggedId) {
        return deltaX;
      }
      if (targetIndex == null || targetIndex === originIndex) {
        return 0;
      }
      if (!draggedWidth) {
        return 0;
      }
      if (targetIndex > originIndex) {
        if (index > originIndex && index <= targetIndex) {
          return -draggedWidth;
        }
      } else {
        if (index >= targetIndex && index < originIndex) {
          return draggedWidth;
        }
      }
      return 0;
    };
    $[13] = deltaX;
    $[14] = draggedId;
    $[15] = draggedWidth;
    $[16] = originIndex;
    $[17] = targetIndex;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const getTranslateX = t9;
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function thRefCallback(id, el_0) {
      if (el_0) {
        thRefs.current.set(id, el_0);
      } else {
        thRefs.current.delete(id);
      }
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const thRefCallback = t10;
  let t11;
  if ($[20] !== draggedId || $[21] !== getTranslateX || $[22] !== handlePointerDown || $[23] !== handlePointerUp || $[24] !== isDraggable) {
    t11 = {
      isDraggable,
      draggedId,
      getTranslateX,
      handlePointerDown,
      handlePointerMove,
      handlePointerUp,
      thRefCallback
    };
    $[20] = draggedId;
    $[21] = getTranslateX;
    $[22] = handlePointerDown;
    $[23] = handlePointerUp;
    $[24] = isDraggable;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
function _temp(h_1) {
  return h_1.id;
}